// A versioned, extensible envelope carried in the OAuth `state` param.
// All payload fields are OPTIONAL so an older reader can ignore unknown fields
// and a newer reader tolerates their absence.
export interface AuthState {
  // CORE FIELDS -- do not change, always required
  // schema version — bump only on breaking changes
  v: number;
  // CSRF/replay binding, verified at the callback
  nonce: string;

  // All other fields are optional and can be consumed by the client elsewhere
  // drives gatekeeper auto-route to /sso/$tenant

  /** this will auto-navigate to the SSO tenant's IdP login page */
  ssoTeam?: string;
  /** this will auto-navigate to the gatekeeper sso/$tenant page with the tenant pre-filled, requires a user click to go to the IdP login page */
  ssoTeamHint?: string;
  /** for analytics purposes to track the originating app */
  ref?: string;
  /** for tracing purposes to correlate requests */
  traceId?: string;
  /** optional org context used to restore sandbox/app selection */
  orgId?: string;
  /** optional location context used to restore sandbox/app selection */
  locationIds?: string[];
  /** set by the desktop app on a shared workstation to require PIN enrollment after authentication */
  requirePinEnrollment?: boolean;

  // additional properties can be added
}

export const AUTH_STATE_VERSION = 1;

export function encodeAuthState(state: AuthState): string {
  return base64UrlEncode(JSON.stringify(state));
}

export function decodeAuthState(stateStr: string): AuthState | undefined {
  try {
    const state = JSON.parse(base64UrlDecode(stateStr));
    // ignore unknown versions so we don't break existing flows
    if (state.v !== AUTH_STATE_VERSION) {
      return;
    }
    return state as AuthState;
  } catch (err) {
    // NOTE intentional console.warn and not sentry.warn because
    // importing sentry causes a circular dependency, it's unlikely to happen, and isn't fatal.
    // Warns show up in Sentry breadcrumbs automatically though.
    console.warn('Error decoding auth state', err);
    return;
  }
}

export function decodeAuthStateFromURL(url?: string, options?: { baseUrl?: string }): AuthState | undefined {
  if (!url) {
    return undefined;
  }
  const urlParams = safeURL(url, options?.baseUrl ?? getDefaultBaseUrl());
  const state = urlParams.searchParams.get('state') || undefined;
  return state ? decodeAuthState(state) : undefined;
}

function getDefaultBaseUrl(): string | undefined {
  if (typeof globalThis !== 'undefined' && 'window' in globalThis) {
    const { window } = globalThis;
    if (window?.location?.origin) {
      return window.location.origin;
    }
  }
  return undefined;
}

function safeURL(url: string, baseUrl?: string): URL {
  try {
    return new URL(url, baseUrl);
  } catch (error) {
    return new URL('about:blank');
  }
}

// Unicode-safe base64url
function base64UrlEncode(text: string): string {
  const bytes = new TextEncoder().encode(text);
  // NOTE when supported, we can remove the below lines and replace with bytes.toBase64()
  let bin = '';
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlDecode(s: string): string {
  // NOTE when supported, we can remove the below lines and replace with Uint8Array.fromBase64(s)
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/'));
  return new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)));
}
